import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Title,
  Group,
  Button,
  Table,
  TextInput,
  Modal,
  Text,
  Badge,
  Stack,
  Alert,
  LoadingOverlay,
  ScrollArea,
  ActionIcon,
  NumberInput,
  Grid,
} from '@mantine/core';
import { useSupabase } from '../contexts/SupabaseContext';
import {
  IconCamera,
  IconSearch,
  IconRefresh,
  IconAlertCircle,
  IconCheck,
  IconEye,
  IconCalendar,
} from '@tabler/icons-react';

const SnapshotManagement = () => {
  const { isConnected: supabaseConnected, error: supabaseError } = useSupabase();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snapshots, setSnapshots] = useState([]);

  // 권한 확인 (임시로 모든 접근 허용)
  const hasAccess = true;
  const [queryModalOpened, setQueryModalOpened] = useState(false);
  const [queryData, setQueryData] = useState({
    snapshotId: '',
    account: '',
  });
  const [queryResult, setQueryResult] = useState(null);

  const loadSnapshots = async () => {
    setLoading(true);
    try {
      // TODO: 실제 컨트랙트에서 데이터 로드
      // 임시 데이터
      setSnapshots([
        {
          id: 1,
          blockNumber: 12345678,
          timestamp: '2024-01-15 10:30:00',
          totalAccounts: 150,
          totalSupply: '1,000,000,000',
        },
        {
          id: 2,
          blockNumber: 12345680,
          timestamp: '2024-01-20 14:45:00',
          totalAccounts: 175,
          totalSupply: '1,000,000,000',
        },
        {
          id: 3,
          blockNumber: 12345685,
          timestamp: '2024-01-25 09:15:00',
          totalAccounts: 200,
          totalSupply: '1,000,000,000',
        },
      ]);
    } catch (error) {
      console.error('Error loading snapshots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSnapshot = async () => {
    try {
      // TODO: 실제 컨트랙트에서 createSnapshot 실행
      console.log('Creating snapshot');
      loadSnapshots();
    } catch (error) {
      console.error('Error creating snapshot:', error);
    }
  };

  const handleQuerySnapshot = async () => {
    if (!queryData.snapshotId || !queryData.account) return;
    
    try {
      // TODO: 실제 컨트랙트에서 스냅샷 조회
      console.log('Querying snapshot:', queryData);
      setQueryResult({
        snapshotId: queryData.snapshotId,
        account: queryData.account,
        balance: '500,000',
        lockedAmount: '50,000',
        blockNumber: 12345678,
      });
    } catch (error) {
      console.error('Error querying snapshot:', error);
    }
  };

  useEffect(() => {
    loadSnapshots();
  }, []);

  const filteredSnapshots = snapshots.filter(snapshot =>
    snapshot.id.toString().includes(searchTerm) ||
    snapshot.blockNumber.toString().includes(searchTerm)
  );

  // 권한이 없는 경우 접근 거부 메시지 표시
  if (!hasAccess) {
    return (
      <Container fluid size="xl" py="md">
        <Alert icon={<IconAlertCircle size="1rem" />} color="red" radius="md">
          <Title order={3} mb="sm">Access Denied</Title>
          <Text>
            You need to connect your wallet and have Owner or Manager privileges to access this page.
          </Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid py="md" pl="xl">
      <Group justify="space-between" mb="md">
        <Title order={2}>Snapshot Management</Title>
        <Group>
          <Button
            leftSection={<IconCamera size="1rem" />}
            onClick={handleCreateSnapshot}
            loading={loading}
          >
            Create Snapshot
          </Button>
          <Button
            leftSection={<IconRefresh size="1rem" />}
            variant="light"
            onClick={loadSnapshots}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      {supabaseError && (
        <Alert icon={<IconAlertCircle size="1rem" />} color="red" radius="md" mb="md">
          Database 연결 오류: {supabaseError}. 데이터를 불러올 수 없습니다.
        </Alert>
      )}

      <Card shadow="sm" padding="lg" radius="md" withBorder mb="xl">
        <Group justify="space-between" mb="md">
          <Title order={3}>Snapshot List</Title>
          <TextInput
            placeholder="Search snapshots..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftSection={<IconSearch size="1rem" />}
            style={{ width: 300 }}
          />
        </Group>

        {filteredSnapshots.length === 0 ? (
          <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
            No snapshots found.
          </Alert>
        ) : (
          <ScrollArea>
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th>Snapshot ID</th>
                  <th>Block Number</th>
                  <th>Timestamp</th>
                  <th>Total Accounts</th>
                  <th>Total Supply</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSnapshots.map((snapshot) => (
                  <tr key={snapshot.id}>
                    <td>
                      <Badge color="blue" size="sm">
                        #{snapshot.id}
                      </Badge>
                    </td>
                    <td>
                      <Text size="sm" ff="monospace">
                        {snapshot.blockNumber.toLocaleString()}
                      </Text>
                    </td>
                    <td>{snapshot.timestamp}</td>
                    <td>{snapshot.totalAccounts}</td>
                    <td>{snapshot.totalSupply}</td>
                    <td>
                      <ActionIcon
                        color="blue"
                        variant="light"
                        onClick={() => setQueryModalOpened(true)}
                      >
                        <IconEye size="1rem" />
                      </ActionIcon>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={3}>Snapshot Query</Title>
          <Button
            leftSection={<IconSearch size="1rem" />}
            onClick={() => setQueryModalOpened(true)}
          >
            Query Snapshot
          </Button>
        </Group>

        {queryResult ? (
          <Stack>
            <Alert icon={<IconCheck size="1rem" />} color="green">
              Query completed successfully
            </Alert>
            <Grid>
              <Grid.Col span={12} md={3}>
                <Card padding="md" withBorder>
                  <Text size="sm" color="dimmed" mb="xs">Snapshot ID</Text>
                  <Text size="lg" weight={600}>#{queryResult.snapshotId}</Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={12} md={3}>
                <Card padding="md" withBorder>
                  <Text size="sm" color="dimmed" mb="xs">Account</Text>
                  <Text size="sm" ff="monospace">{queryResult.account}</Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={12} md={3}>
                <Card padding="md" withBorder>
                  <Text size="sm" color="dimmed" mb="xs">Balance</Text>
                  <Text size="lg" weight={600}>{queryResult.balance}</Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={12} md={3}>
                <Card padding="md" withBorder>
                  <Text size="sm" color="dimmed" mb="xs">Block Number</Text>
                  <Text size="sm" ff="monospace">{queryResult.blockNumber.toLocaleString()}</Text>
                </Card>
              </Grid.Col>
            </Grid>
          </Stack>
        ) : (
          <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
            Select a snapshot ID and enter an account address to query balance information.
          </Alert>
        )}
      </Card>

      <Modal
        opened={queryModalOpened}
        onClose={() => setQueryModalOpened(false)}
        title="Query Snapshot Balance"
        size="md"
      >
        <Stack>
          <NumberInput
            label="Snapshot ID"
            placeholder="1"
            value={queryData.snapshotId}
            onChange={(value) => setQueryData({ ...queryData, snapshotId: value || '' })}
            min={1}
            required
          />
          <TextInput
            label="Account Address"
            placeholder="0x..."
            value={queryData.account}
            onChange={(e) => setQueryData({ ...queryData, account: e.target.value })}
            required
          />
          
          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={() => setQueryModalOpened(false)}>
              Cancel
            </Button>
            <Button
              leftSection={<IconSearch size="1rem" />}
              onClick={handleQuerySnapshot}
              disabled={!queryData.snapshotId || !queryData.account}
            >
              Query
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default SnapshotManagement;
